import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Inbox, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ch_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setQuotes([]);
  }, []);

  const fetchQuotes = useCallback(async (tk) => {
    setLoadingQuotes(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${tk}` } });
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      setLoadingQuotes(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchQuotes(token);
  }, [token, fetchQuotes]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-foreground/25 py-4 text-lg focus:border-primary focus:outline-none transition-colors duration-300";

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <a href="/" className="font-serif text-2xl tracking-tight">Cozy Home<span className="text-primary">.</span></a>
          <h1 className="font-serif text-4xl tracking-tight mt-8 mb-2">Admin</h1>
          <p className="text-muted-foreground mb-10">Sign in to view quote requests.</p>
          <div className="space-y-6">
            <input data-testid="admin-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
          {error && <p data-testid="admin-error" className="mt-6 text-sm text-destructive">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="group mt-10 w-full inline-flex items-center justify-center gap-3 bg-foreground text-background py-4 text-sm tracking-wide hover:bg-primary transition-colors duration-300 disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <a href="/" className="font-serif text-2xl tracking-tight">Cozy Home<span className="text-primary">.</span></a>
          <div className="flex items-center gap-4">
            <button data-testid="admin-refresh" onClick={() => fetchQuotes(token)} className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground transition-colors">
              <RefreshCw className={`w-4 h-4 ${loadingQuotes ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm border border-border px-4 py-2 hover:bg-foreground hover:text-background transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h1 className="font-serif text-5xl tracking-tight">Quote requests</h1>
            <p className="text-muted-foreground mt-2" data-testid="admin-count">{quotes.length} total</p>
          </div>
        </div>

        {quotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-muted-foreground border border-dashed border-border">
            <Inbox className="w-10 h-10 mb-4" />
            <p>{loadingQuotes ? "Loading…" : "No quote requests yet."}</p>
          </div>
        ) : (
          <div className="border border-border overflow-x-auto bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border uppercase text-xs tracking-widest">
                  <th className="py-4 px-4 font-medium">Date</th>
                  <th className="py-4 px-4 font-medium">Name</th>
                  <th className="py-4 px-4 font-medium">Contact</th>
                  <th className="py-4 px-4 font-medium">Service</th>
                  <th className="py-4 px-4 font-medium">Frequency</th>
                  <th className="py-4 px-4 font-medium">Message</th>
                  <th className="py-4 px-4 font-medium">Lang</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-border/60 hover:bg-muted/50 transition-colors" data-testid="quote-row">
                    <td className="py-4 px-4 whitespace-nowrap text-muted-foreground">{fmtDate(q.created_at)}</td>
                    <td className="py-4 px-4 font-medium">{q.name}</td>
                    <td className="py-4 px-4">
                      <a href={`mailto:${q.email}`} className="text-primary hover:underline block">{q.email}</a>
                      {q.phone && <span className="text-muted-foreground">{q.phone}</span>}
                    </td>
                    <td className="py-4 px-4">{q.service}</td>
                    <td className="py-4 px-4">
                      {q.frequency ? (
                        <span className={q.frequency.toLowerCase().includes("week") && q.frequency.includes("10") ? "text-primary font-medium" : ""}>{q.frequency}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-4 px-4 max-w-xs text-muted-foreground">{q.message || "—"}</td>
                    <td className="py-4 px-4 uppercase text-muted-foreground">{q.locale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
